import React, { useState } from 'react';
import {
  Button,
  Form,
  FormGroup,
  Input,
  Alert,
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Label,
  Spinner
} from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import '../../assets/css/LoginForm.css';
import bg from "../../assets/images/bg.jpg";
import logo from "../../assets/images/logo.png";
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { authActions } from '../../sagas/authSlice';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { FaLock, FaUser, FaEye, FaEyeSlash, FaShieldAlt } from 'react-icons/fa';
import * as Yup from 'yup';
import { PRODUCTS_PAGE } from '../../routes/routeConstants';

const LoginForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const loading = useSelector((state) => state.auth.loading);

  // Validation schema
  const validationSchema = Yup.object({
    username: Yup.string()
      .required(t('auth.usernameRequired', 'Nom d\'utilisateur requis'))
      .min(3, t('auth.usernameMinLength', 'Minimum 3 caractères')),
    password: Yup.string()
      .required(t('auth.passwordRequired', 'Mot de passe requis'))
      .min(4, t('auth.passwordMinLength', 'Minimum 4 caractères'))
  });

  const formik = useFormik({
    initialValues: { username: "", password: "" },
    validationSchema,
    onSubmit: (values) => {
      dispatch(
        authActions.loginRequest({
          user: values,
          onSuccess: (data) => {
            // Success animation
            Swal.fire({
              icon: 'success',
              title: t('auth.loginSuccess', 'Connexion réussie'),
              text: t('auth.welcomeMessage', 'Bienvenue dans MatjarPOS'),
              timer: 1500,
              showConfirmButton: false,
              timerProgressBar: true
            }).then(() => {
              if (data.role === 'ROLE_ADMIN') {
                navigate("/dashboard");
              } else {
                navigate(PRODUCTS_PAGE);
              }
            });
          },
          onError: (error) => {
            Swal.fire({
              icon: 'error',
              title: t('auth.loginError', 'Erreur de connexion'),
              text: error?.message || t('auth.invalidCredentials', 'Identifiants invalides'),
              confirmButtonColor: '#dc3545',
            });
          }
        })
      );
    },
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="modern-auth-wrapper">
      {/* Animated Background */}
      <div className="auth-background-container">
        <div 
          className="auth-background-image"
          style={{ backgroundImage: `url(${bg})` }}
        />
        <div className="auth-gradient-overlay" />
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </div>

      {/* Main Content */}
      <Container fluid className="auth-container">
        <Row className="min-vh-100 align-items-center justify-content-center">
          <Col xs={11} sm={8} md={6} lg={5} xl={4}>
            <div className="login-card-wrapper">
              <Card className="modern-login-card shadow-lg border-0">
                <CardBody className="p-0">
                  {/* Header Section */}
                  <div className="login-header">
                    <div className="logo-container">
                      <div className="logo-wrapper">
                        <img
                          src={logo}
                          alt="MatjarPos"
                          className="login-logo"
                        />
                      </div>
                      <div className="brand-info">
                        <h3 className="brand-name">MatjarPOS</h3>
                        <p className="brand-tagline">
                          {t('auth.tagline', 'Votre solution de gestion commerciale')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Form Section */}
                  <div className="login-form-section">
                    <div className="form-header">
                      <h4 className="form-title">
                        <FaShieldAlt className="me-2 text-primary" />
                        {t('auth.loginTitle', 'Connexion sécurisée')}
                      </h4>
                      <p className="form-subtitle">
                        {t('auth.loginSubtitle', 'Accédez à votre espace de travail')}
                      </p>
                    </div>

                    <Form onSubmit={formik.handleSubmit} className="modern-form">
                      {/* Username Field */}
                      <FormGroup className="form-group-modern">
                        <Label className="form-label-modern">
                          {t('auth.username', 'Nom d\'utilisateur')}
                        </Label>
                        <div className="input-wrapper">
                          <div className="input-icon">
                            <FaUser />
                          </div>
                          <Input
                            type="text"
                            id="username"
                            placeholder={t('auth.usernamePlaceholder', 'Entrez votre nom d\'utilisateur')}
                            className={`form-control-modern ${
                              formik.touched.username && formik.errors.username ? 'is-invalid' : ''
                            } ${
                              formik.touched.username && !formik.errors.username ? 'is-valid' : ''
                            }`}
                            {...formik.getFieldProps('username')}
                          />
                          {formik.touched.username && formik.errors.username && (
                            <div className="invalid-feedback">
                              {formik.errors.username}
                            </div>
                          )}
                        </div>
                      </FormGroup>

                      {/* Password Field */}
                      <FormGroup className="form-group-modern">
                        <Label className="form-label-modern">
                          {t('auth.password', 'Mot de passe')}
                        </Label>
                        <div className="input-wrapper">
                          <div className="input-icon">
                            <FaLock />
                          </div>
                          <Input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            placeholder={t('auth.passwordPlaceholder', 'Entrez votre mot de passe')}
                            className={`form-control-modern ${
                              formik.touched.password && formik.errors.password ? 'is-invalid' : ''
                            } ${
                              formik.touched.password && !formik.errors.password ? 'is-valid' : ''
                            }`}
                            {...formik.getFieldProps('password')}
                          />
                          <button
                            type="button"
                            className="password-toggle"
                            onClick={togglePasswordVisibility}
                            tabIndex={-1}
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                          {formik.touched.password && formik.errors.password && (
                            <div className="invalid-feedback">
                              {formik.errors.password}
                            </div>
                          )}
                        </div>
                      </FormGroup>

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        className="login-button-modern"
                        disabled={loading || !formik.isValid}
                        block
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            {t('auth.loggingIn', 'Connexion en cours...')}
                          </>
                        ) : (
                          <>
                            <FaShieldAlt className="me-2" />
                            {t('auth.loginButton', 'Se connecter')}
                          </>
                        )}
                      </Button>
                    </Form>

                    {/* Footer Links */}
                    <div className="login-footer">
                      {/* <div className="footer-links">
                        <Link to="/forgot-password" className="footer-link">
                          {t('auth.forgotPassword', 'Mot de passe oublié ?')}
                        </Link>
                      </div> */}
                      <div className="footer-info">
                        <small className="text-muted">
                          {t('auth.secureConnection', 'Connexion sécurisée SSL')}
                        </small>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-3 text-white">
              {t('auth.authenticating', 'Authentification en cours...')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
